// Render dei blocchi "rich" (stile WordPress) sul sito pubblico
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ChevronLeft, ChevronRight, Quote, Star, Heart, Trophy, Flag, Shield, Target, Zap, Users, Award, Calendar, MapPin, Camera, Book, Handshake } from 'lucide-react';

const ICON_MAP = { Star, Heart, Trophy, Flag, Shield, Target, Zap, Users, Award, Calendar, MapPin, Camera, Book, Handshake, Quote };

// Applica gli stili personalizzati del blocco (bg, colore, allineamento, padding, radius)
function styleOf(b) {
  const s = b.style || {};
  const css = {};
  if (s.bg) css.backgroundColor = s.bg;
  if (s.color) css.color = s.color;
  if (s.align) css.textAlign = s.align;
  if (s.paddingY != null) css.paddingTop = css.paddingBottom = s.paddingY + 'px';
  if (s.paddingX != null) css.paddingLeft = css.paddingRight = s.paddingX + 'px';
  if (s.radius != null) css.borderRadius = s.radius + 'px';
  if (s.maxWidth) css.maxWidth = s.maxWidth + 'px';
  return css;
}
function wrapperCls(b) {
  const s = b.style || {};
  let cls = '';
  if (s.hideMobile) cls += ' hidden md:block';
  if (s.hideDesktop) cls += ' md:hidden';
  return cls;
}

export function RichBlock({ block }) {
  const b = block;
  const t = b.type;

  if (t === 'heading') {
    const Tag = `h${b.level || 2}`;
    const base = 'font-display font-bold';
    const sizes = { 1: 'text-4xl sm:text-5xl lg:text-6xl', 2: 'text-3xl sm:text-4xl lg:text-5xl', 3: 'text-2xl sm:text-3xl', 4: 'text-xl sm:text-2xl' };
    return (
      <section className={`py-6 ${wrapperCls(b)}`} style={styleOf(b)}>
        <div className="max-w-5xl mx-auto px-4 lg:px-8">
          <Tag className={`${base} ${sizes[b.level || 2]} text-white uppercase`}>{b.text || 'Titolo'}</Tag>
          {b.underline !== false && <div className="w-20 h-1 bg-pink-brand rounded mt-2" style={{ marginLeft: b.style?.align === 'center' ? 'auto' : 0, marginRight: b.style?.align === 'center' ? 'auto' : 0 }} />}
        </div>
      </section>
    );
  }
  if (t === 'paragraph') {
    // Se il blocco è stato creato con il rich editor, "html" === true e content è già HTML.
    // Altrimenti applichiamo il vecchio mini-markdown (compatibilità all'indietro).
    const html = b.html ? (b.content || '') : simpleMarkdown(b.content || '');
    return (
      <section className={`py-4 ${wrapperCls(b)}`} style={styleOf(b)}>
        <div className="max-w-4xl mx-auto px-4 lg:px-8 text-white/85 leading-relaxed rte-output" dangerouslySetInnerHTML={{ __html: html }} />
      </section>
    );
  }
  if (t === 'button') {
    if (!b.label) return null;
    const inner = <>{b.label}</>;
    const cls = 'btn-primary inline-flex';
    return (
      <section className={`py-4 ${wrapperCls(b)}`} style={styleOf(b)}>
        <div className="max-w-5xl mx-auto px-4 lg:px-8">
          {b.url && b.url.startsWith('http')
            ? <a href={b.url} target="_blank" rel="noreferrer" className={cls}>{inner}</a>
            : <Link to={b.url || '/'} className={cls}>{inner}</Link>}
        </div>
      </section>
    );
  }
  if (t === 'separator') {
    return <div className={`py-3 ${wrapperCls(b)}`}><div className="max-w-5xl mx-auto px-4 lg:px-8"><hr className="border-white/15" /></div></div>;
  }
  if (t === 'spacer') {
    return <div className={wrapperCls(b)} style={{ height: (b.height || 40) + 'px' }} />;
  }
  if (t === 'quote') {
    return (
      <section className={`py-6 ${wrapperCls(b)}`} style={styleOf(b)}>
        <blockquote className="max-w-4xl mx-auto px-6 lg:px-8 border-l-4 border-pink-brand">
          <Quote size={24} className="text-pink-brand mb-2" />
          <p className="text-white/90 text-lg italic">{b.text}</p>
          {b.author && <cite className="block mt-2 text-white/60 text-sm not-italic">— {b.author}</cite>}
        </blockquote>
      </section>
    );
  }
  if (t === 'list') {
    const items = (b.items || []).filter(Boolean);
    if (items.length === 0) return null;
    const Tag = b.ordered ? 'ol' : 'ul';
    return (
      <section className={`py-4 ${wrapperCls(b)}`} style={styleOf(b)}>
        <div className="max-w-4xl mx-auto px-4 lg:px-8">
          <Tag className={`${b.ordered ? 'list-decimal' : 'list-disc'} ml-5 text-white/85 space-y-1`}>
            {items.map((it, i) => <li key={i}>{it}</li>)}
          </Tag>
        </div>
      </section>
    );
  }
  if (t === 'card_group') {
    const cards = b.cards || [];
    if (cards.length === 0) return null;
    const cols = b.columns || Math.min(cards.length, 4);
    const gridCls = { 1: 'grid-cols-1', 2: 'sm:grid-cols-2', 3: 'sm:grid-cols-2 lg:grid-cols-3', 4: 'sm:grid-cols-2 lg:grid-cols-4' }[cols] || 'sm:grid-cols-2 lg:grid-cols-3';
    return (
      <section className={`py-10 ${wrapperCls(b)}`} style={styleOf(b)}>
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          {b.title && <h2 className="section-title centered text-3xl md:text-4xl text-center mb-8">{b.title}</h2>}
          <div className={`grid gap-5 ${gridCls}`}>
            {cards.map((c, i) => {
              const Ic = ICON_MAP[c.icon] || Star;
              return (
                <div key={i} className="glass-card p-6 text-center">
                  <div className="w-14 h-14 mx-auto mb-3 rounded-xl bg-pink-brand flex items-center justify-center">
                    <Ic size={22} className="text-white" />
                  </div>
                  <h3 className="text-white font-bold text-lg mb-1">{c.title}</h3>
                  <p className="text-white/70 text-sm">{c.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>
    );
  }
  if (t === 'columns') {
    const cols = b.columns || 2;
    const contents = b.contents || [];
    return (
      <section className={`py-8 ${wrapperCls(b)}`} style={styleOf(b)}>
        <div className="max-w-6xl mx-auto px-4 lg:px-8">
          <div className={`grid gap-6 ${cols === 3 ? 'md:grid-cols-3' : cols === 4 ? 'md:grid-cols-2 lg:grid-cols-4' : 'md:grid-cols-2'}`}>
            {Array.from({ length: cols }).map((_, i) => (
              <div key={i} className="text-white/85" dangerouslySetInnerHTML={{ __html: simpleMarkdown(contents[i] || '') }} />
            ))}
          </div>
        </div>
      </section>
    );
  }
  if (t === 'accordion') {
    const items = b.items || [];
    return (
      <section className={`py-8 ${wrapperCls(b)}`} style={styleOf(b)}>
        <div className="max-w-4xl mx-auto px-4 lg:px-8 space-y-2">
          {items.map((it, i) => <AccordionItem key={i} title={it.title} content={it.content} />)}
        </div>
      </section>
    );
  }
  if (t === 'html') {
    return (
      <section className={`py-4 ${wrapperCls(b)}`} style={styleOf(b)}>
        <div className="max-w-6xl mx-auto px-4 lg:px-8" dangerouslySetInnerHTML={{ __html: b.code || '' }} />
      </section>
    );
  }
  if (t === 'gallery_carousel') {
    const items = (b.items || []).filter((it) => it.src);
    if (items.length === 0) return null;
    return <Carousel items={items} block={b} />;
  }

  return null;
}

function Carousel({ items, block }) {
  const desired = block.itemsPerView || 3;
  const [idx, setIdx] = useState(0);
  const [perView, setPerView] = useState(desired);

  // Adatta il numero di immagini visibili alla larghezza schermo
  // mobile: sempre 1 — tablet: max 2 — desktop: il valore impostato (max 4)
  useEffect(() => {
    const onResize = () => {
      const w = window.innerWidth;
      if (w < 768) setPerView(1);
      else if (w < 1024) setPerView(Math.min(desired, 2));
      else setPerView(desired);
    };
    window.addEventListener('resize', onResize);
    onResize();
    return () => window.removeEventListener('resize', onResize);
  }, [desired]);

  const prev = () => setIdx((i) => (i - 1 + items.length) % items.length);
  const next = () => setIdx((i) => (i + 1) % items.length);
  const shown = Array.from({ length: Math.min(perView, items.length) }, (_, i) => items[(idx + i) % items.length]);

  const gridCls = perView === 1
    ? 'grid-cols-1'
    : perView === 2
      ? 'grid-cols-1 md:grid-cols-2'
      : perView === 3
        ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'
        : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4';

  return (
    <section className={`py-10 ${wrapperCls(block)}`} style={styleOf(block)}>
      <div className="max-w-7xl mx-auto px-4 lg:px-8">
        {block.title && <h2 className="section-title centered text-3xl md:text-4xl text-center mb-6">{block.title}</h2>}
        <div className="relative">
          <button onClick={prev} className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 z-10 w-11 h-11 rounded-full bg-pink-brand text-white flex items-center justify-center shadow-lg" aria-label="Precedente"><ChevronLeft size={20} /></button>
          <div className={`grid gap-3 items-center ${gridCls}`}>
            {shown.map((it, i) => (
              <div key={`${i}-${idx}`} className="bg-navy-dark/40 rounded-xl overflow-hidden flex items-center justify-center">
                <img
                  src={it.src}
                  alt={it.alt || ''}
                  className="w-full h-auto max-h-[60vh] object-contain"
                  loading="lazy"
                />
              </div>
            ))}
          </div>
          <button onClick={next} className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 z-10 w-11 h-11 rounded-full bg-pink-brand text-white flex items-center justify-center shadow-lg" aria-label="Successivo"><ChevronRight size={20} /></button>
        </div>
      </div>
    </section>
  );
}

function AccordionItem({ title, content }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="bg-navy-dark border border-white/10 rounded-lg">
      <button onClick={() => setOpen((o) => !o)} className="w-full p-4 flex items-center justify-between text-left">
        <span className="text-white font-semibold">{title}</span>
        <ChevronDown size={18} className={`text-pink-brand transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="p-4 pt-0 text-white/75 text-sm leading-relaxed" dangerouslySetInnerHTML={{ __html: simpleMarkdown(content || '') }} />
      )}
    </div>
  );
}

export function simpleMarkdown(s) {
  const escapeHtml = (str) => str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  return escapeHtml(s).split(/\n{2,}/).map((p) => {
    let out = p
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      .replace(/(^|[^*])\*(?!\*)([^*]+)\*/g, '$1<em>$2</em>')
      .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noreferrer" class="text-pink-brand underline">$1</a>')
      .replace(/\n/g, '<br/>');
    return `<p>${out}</p>`;
  }).join('');
}
